// ABOUTME: Admin interface for managing knowledge base tags
// ABOUTME: Simple list view with create and delete operations

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { getKBTags, createKBTag, deleteKBTag } from '../../../services/api';
import { KBTagDto } from '../../../types/knowledgeBase';

const TagManager: React.FC = () => {
  const [tags, setTags] = useState<KBTagDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<KBTagDto | null>(null);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);

  // Form state
  const [tagName, setTagName] = useState('');

  // Helper function to detect if form has changed
  const hasFormChanged = (): boolean => {
    return tagName.trim() !== '';
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const tagsData = await getKBTags();
      setTags(tagsData);
    } catch (err) {
      console.error('Failed to load tags:', err);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setTagName('');
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (hasFormChanged()) {
      setUnsavedChangesDialogOpen(true);
    } else {
      setCreateDialogOpen(false);
      setTagName('');
    }
  };

  const handleConfirmClose = () => {
    setUnsavedChangesDialogOpen(false);
    setCreateDialogOpen(false);
    setTagName('');
  };

  const handleCancelClose = () => {
    setUnsavedChangesDialogOpen(false);
  };

  const handleCreateTag = async () => {
    if (!tagName.trim()) return;

    setSaving(true);
    try {
      await createKBTag(tagName.trim());
      // Close dialog directly after successful save
      setCreateDialogOpen(false);
      setTagName('');
      loadData();
    } catch (err) {
      console.error('Failed to create tag:', err);
      setError('Failed to create tag');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (tag: KBTagDto) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTagToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;

    try {
      await deleteKBTag(tagToDelete.id);
      handleCloseDeleteDialog();
      loadData();
    } catch (err) {
      console.error('Failed to delete tag:', err);
      setError('Failed to delete tag');
      handleCloseDeleteDialog();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5">Tags</Typography>
          <Typography variant="body2" color="text.secondary">
            Tags are automatically created when assigning to content
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
        >
          Create Tag
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tags Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Usage Count</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No tags found. Tags will be created when you assign them to content.
                </TableCell>
              </TableRow>
            ) : (
              tags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell>{tag.name}</TableCell>
                  <TableCell align="right">{tag.usageCount}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(tag)}
                      title="Delete tag"
                      aria-label="Delete tag"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Tag</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Tag Name"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              required
              fullWidth
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter' && tagName.trim()) {
                  handleCreateTag();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreateTag} variant="contained" disabled={saving || !tagName.trim()}>
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tag &quot;{tagToDelete?.name}&quot;?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will remove the tag from all associated content.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={unsavedChangesDialogOpen} onClose={handleCancelClose}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>Continue Editing</Button>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagManager;
